// src/pages/Login.jsx (or wherever your Login component is located)
import React, { useState } from "react";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { auth, provider, db } from "../config/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

import { useDispatch } from "react-redux";
import { setLogin } from "../store/slices/userSlice"; // <-- import the action

import { useNavigate, useLocation } from "react-router-dom"; // for navigation

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination from location state, or default to /statistics
  const from = location.state?.from?.pathname || "/statistics";

  // Function to fetch user settings including pro member status
  const fetchUserSettings = async (userUid) => {
    if (!userUid) {
      console.error("No user uid provided to fetchUserSettings");
      return { proMember: false };
    }

    try {
      // Get user data from the userInfo collection (main document)
      const userDataRef = doc(db, "userInfo", userUid);
      const userDataSnap = await getDoc(userDataRef);

      const userData = userDataSnap.exists() ? userDataSnap.data() : {};

      // Get pro status and username from main user document only
      const isProUser = userData.isProUser || false;
      const username = userData.username || null;

      return {
        proMember: isProUser,
        username: username,
      };
    } catch (error) {
      console.error("Error fetching user settings:", error);
      return { proMember: false, username: null };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const userCredential = await signInWithPopup(auth, provider);

      // Fetch user settings
      const userSettings = await fetchUserSettings(userCredential.user.uid);

      const userInfo = {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        accessToken: userCredential.user.accessToken,
        photoUrl: userCredential.user.photoURL,
        emailVerified: userCredential.user.emailVerified,
        displayName: userCredential.user.displayName,
        firstName: userCredential.user.displayName?.split(" ")[0] || null,
        lastName:
          userCredential.user.displayName?.split(" ").slice(1).join(" ") ||
          null,
        proMember: userSettings.proMember,
        username: userSettings.username,
      };

      // Update user info in Firestore with Google profile data
      await setDoc(
        doc(db, "userInfo", userCredential.user.uid),
        {
          email: userCredential.user.email,
          photoUrl: userCredential.user.photoURL,
          displayName: userCredential.user.displayName,
          firstName: userCredential.user.displayName?.split(" ")[0] || null,
          lastName:
            userCredential.user.displayName?.split(" ").slice(1).join(" ") ||
            null,
          username: userSettings.username,
        },
        { merge: true }
      );

      dispatch(setLogin(userInfo));
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Error logging in:", error.message);
      setError(error.message);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Fetch user settings
      const userSettings = await fetchUserSettings(userCredential.user.uid);

      // Get user info from the response
      const userInfo = {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        proMember: userSettings.proMember,
        username: userSettings.username,
      };

      // Dispatch the setLogin action to store userInfo in Redux
      dispatch(setLogin(userInfo));

      // Navigate to the protected page
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Error logging in:", error.message);
      setError(error.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4">
      <div className="max-w-md w-full bg-secondary rounded-xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-center text-white mb-6">
          Login
        </h2>

        {error && (
          <div className="bg-red-500 text-white p-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-white mb-1"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 bg-primary border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-test"
              required
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-white mb-1"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 bg-primary border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-test"
              required
            />
          </div>

          <button
            type="submit"
            className="w-full bg-test hover:bg-test-dark text-primary font-medium py-2 px-4 rounded-lg transition duration-200"
          >
            Login
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-secondary text-white">
                Or continue with
              </span>
            </div>
          </div>

          <button
            onClick={signInWithGoogle}
            className="mt-4 w-full flex items-center justify-center bg-white hover:bg-gray-100 text-gray-800 font-medium py-2 px-4 rounded-lg transition duration-200"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
              />
            </svg>
            Google
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
